'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const liveSessionCards = [
    {
        label: 'Live Now',
        emoji: '🔴',
        color: 'bg-orange/10 border-orange/20 text-orange',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b34a8885-1773877148882.png",
        alt: 'Chef teaching live cooking class in a warm professional kitchen, flames and pans, close-up hands preparing food',
        title: 'Nigerian Cooking',
        sub: 'Live · Tue & Thu 6pm'
    },
    {
        label: 'Bootcamp',
        emoji: '🚀',
        color: 'bg-primary/10 border-primary/20 text-primary',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_1562a0252-1772507389010.png",
        alt: 'Developer teaching live programming session on laptop, bright screen, code visible, modern workspace',
        title: 'Full-Stack Dev',
        sub: 'Bootcamp · 8 weeks'
    },
    {
        label: 'Book a Slot',
        emoji: '📅',
        color: 'bg-accent/10 border-accent/20 text-accent',
        img: "https://images.unsplash.com/photo-1578058405567-53aea0a6499b",
        alt: 'Carpenter teaching live woodworking in a workshop, sawdust, warm workshop lighting, tools on bench',
        title: 'Carpentry Basics',
        sub: 'Pick a time · 1-on-1'
    }];


export default function HeroSection() {
    const blob1Ref = useRef<HTMLDivElement>(null);
    const blob2Ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let raf: number;
        let mouseX = 0;
        let mouseY = 0;
        let currentX = 0;
        let currentY = 0;

        const handleMouseMove = (e: MouseEvent) => {
            mouseX = (e.clientX / window.innerWidth - 0.5) * 60;
            mouseY = (e.clientY / window.innerHeight - 0.5) * 40;
        };

        const animate = () => {
            currentX += (mouseX - currentX) * 0.04;
            currentY += (mouseY - currentY) * 0.04;
            if (blob1Ref.current) {
                blob1Ref.current.style.transform = `translate(${currentX * 0.6}px, ${currentY * 0.6}px)`;
            }
            if (blob2Ref.current) {
                blob2Ref.current.style.transform = `translate(${-currentX * 0.4}px, ${-currentY * 0.4}px)`;
            }
            raf = requestAnimationFrame(animate);
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        raf = requestAnimationFrame(animate);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(raf);
        };
    }, []);

    return (
        <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background pt-16">
            {/* Atmospheric background */}
            <div className="absolute inset-0 grid-bg pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                    ref={blob1Ref}
                    className="absolute top-[-10%] left-[-5%] w-[700px] h-[700px] blob-primary animate-blob will-change-transform" />

                <div
                    ref={blob2Ref}
                    className="absolute bottom-[-15%] right-[-10%] w-[600px] h-[600px] blob-accent animate-blob-delay will-change-transform" />

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] blob-orange opacity-40" />
                <div className="noise-overlay absolute inset-0" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 md:py-20">
                    {/* Left — Text */}
                    <div className="flex flex-col gap-6 md:gap-8">
                        {/* Eyebrow */}
                        <div className="animate-hero-1">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-blue text-primary text-sm font-semibold">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                                </span>
                                The Connected Learning Ecosystem
                            </span>
                        </div>

                        {/* Headline */}
                        <div className="animate-hero-2">
                            <h1 className="text-hero font-extrabold text-foreground">
                                Learning works{' '}
                                <span className="text-gradient-blue">better</span>{' '}
                                connected.
                            </h1>
                        </div>

                        {/* Supporting copy */}
                        <div className="animate-hero-3">
                            <p className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed max-w-xl">
                                Learn something new. Teach what you know. Build skills that matter. Anytime. Anywhere{' '}
                                <span className="text-foreground font-medium">
                                    Wisly connects learners, instructors, creators, and organizations
                                    in one place to discover, create, and experience learning.
                                </span>
                            </p>
                        </div>

                        {/* CTAs */}
                        <div className="animate-hero-4 flex flex-col sm:flex-row gap-3">
                            <Link
                                href="/learn"
                                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 shadow-blue hover:shadow-blue-lg hover:-translate-y-0.5 transition-all duration-300"
                            >
                                Explore learning

                                <svg
                                    className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        d="M5 12h14M12 5l7 7-7 7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Link>

                            <Link
                                href="/studio"
                                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-card text-foreground border border-border rounded-full text-base font-semibold hover:border-primary/30 hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-300 shadow-card"
                            >
                                Start teaching
                            </Link>
                        </div>

                        {/* Learning types */}
                        <div className="animate-hero-5 flex flex-wrap gap-2">
                            {[
                                'Courses',
                                'Live Sessions',
                                'Bootcamps',
                                'Programs'
                            ].map((p, i) => {
                                const colors = [
                                    'bg-primary/8 text-primary border-primary/15',
                                    'bg-purple/8 text-purple border-purple/15',
                                    'bg-orange/8 text-orange border-orange/15',
                                    'bg-green/8 text-green border-green/15'
                                ];

                                return (
                                    <span
                                        key={p}
                                        className={`pill-tag border ${colors[i]}`}
                                    >
                                        {p}
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right — Floating live session cards */}
                    <div className="relative h-[460px] md:h-[540px] animate-hero-5">
                        {/* Center glow */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-72 h-72 blob-primary opacity-60 rounded-full" />
                        </div>

                        {/* Card 1 — top-left */}
                        <div className="absolute top-0 left-0 w-44 md:w-52 animate-float z-20">
                            <div className="glass-light rounded-2xl overflow-hidden shadow-card-lg border border-white/60">
                                <div className="h-28 overflow-hidden relative">
                                    <AppImage
                                        src={liveSessionCards[0].img}
                                        alt={liveSessionCards[0].alt}
                                        width={200}
                                        height={112}
                                        className="w-full h-full object-cover" />
                                    <div className="absolute top-2 right-2">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                                            LIVE
                                        </span>
                                    </div>
                                </div>
                                <div className="p-3">
                                    <p className="font-bold text-foreground text-sm">{liveSessionCards[0].title}</p>
                                    <p className="text-muted-foreground text-xs mt-0.5">{liveSessionCards[0].sub}</p>
                                    <span className={`mt-2 pill-tag border ${liveSessionCards[0].color}`}>
                                        {liveSessionCards[0].label}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Card 2 — center-right */}
                        <div className="absolute top-16 right-0 w-44 md:w-52 animate-float-delayed z-20">
                            <div className="glass-light rounded-2xl overflow-hidden shadow-card-lg border border-white/60">
                                <div className="h-28 overflow-hidden">
                                    <AppImage
                                        src={liveSessionCards[1].img}
                                        alt={liveSessionCards[1].alt}
                                        width={200}
                                        height={112}
                                        className="w-full h-full object-cover" />
                                </div>
                                <div className="p-3">
                                    <p className="font-bold text-foreground text-sm">{liveSessionCards[1].title}</p>
                                    <p className="text-muted-foreground text-xs mt-0.5">{liveSessionCards[1].sub}</p>
                                    <span className={`mt-2 pill-tag border ${liveSessionCards[1].color}`}>
                                        {liveSessionCards[1].label}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Card 3 — bottom-center */}
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-44 md:w-52 animate-float-slow z-20">
                            <div className="glass-light rounded-2xl overflow-hidden shadow-card-lg border border-white/60">
                                <div className="h-28 overflow-hidden">
                                    <AppImage
                                        src={liveSessionCards[2].img}
                                        alt={liveSessionCards[2].alt}
                                        width={200}
                                        height={112}
                                        className="w-full h-full object-cover" />
                                </div>
                                <div className="p-3">
                                    <p className="font-bold text-foreground text-sm">{liveSessionCards[2].title}</p>
                                    <p className="text-muted-foreground text-xs mt-0.5">{liveSessionCards[2].sub}</p>
                                    <span className={`mt-2 pill-tag border ${liveSessionCards[2].color}`}>
                                        {liveSessionCards[2].label}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Central platform badge */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                            <div className="glass-light rounded-2xl p-4 shadow-card-xl border border-white/70 text-center min-w-[120px]">
                                <AppImage
                                    src="/assets/images/app_logo.png"
                                    alt="Wisly live learning platform logo"
                                    width={40}
                                    height={40}
                                    className="mx-auto rounded-xl mb-2" />

                                <p className="font-bold text-foreground text-sm">Wisly</p>
                                <p className="text-primary text-xs font-semibold">All live classes</p>
                            </div>
                        </div>

                        {/* Stats floating pill */}
                        <div className="absolute bottom-20 right-4 z-30 animate-float">
                            <div className="glass-light rounded-full px-4 py-2 shadow-card border border-white/60 flex items-center gap-2">
                                <span className="text-green text-sm font-bold">↑ 12k+</span>
                                <span className="text-foreground text-xs font-medium">live learners</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="relative z-10 flex flex-col items-center pb-8 gap-2 animate-hero-5">
                <p className="text-xs text-muted-foreground font-medium tracking-widest uppercase">Scroll</p>
                <div className="w-px h-8 bg-gradient-to-b from-muted-foreground/40 to-transparent animate-scroll-bounce" />
            </div>
        </section>);

}